import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/feature/DashboardLayout';
import { useAuth } from '../../lib/api/auth';
import { vipApi, paymentApi } from '../../lib/api/endpoints';
import type { VIPInfo, PaymentInfo } from '../../lib/api/types';

interface BenefitItem {
  name: string;
  description: string;
  free_value: string;
  vip_value: string;
}

interface PackageItem {
  id: string;
  name: string;
  description: string;
  months: number;
  price: number | string;
  original_price: string;
  is_popular: boolean;
}

interface VipRightsData {
  free: BenefitItem[];
  vip: BenefitItem[];
  packages: PackageItem[];
}

const defaultBenefits: BenefitItem[] = [
  { name: '儿童数量', description: '最多添加儿童', free_value: '1个', vip_value: '不限' },
  { name: '奖惩规则', description: '最多创建规则', free_value: '20条', vip_value: '不限' },
  { name: '积分兑换', description: '最多兑换项', free_value: '5个', vip_value: '不限' },
  { name: 'AI对话', description: '每周AI调用', free_value: '10次', vip_value: '不限' },
  { name: '广告', description: '广告展示', free_value: '有', vip_value: '无' },
];

const defaultPackages: PackageItem[] = [
  { id: 'month', name: '月卡', description: '1个月VIP会员', months: 1, price: 12, original_price: '15.00', is_popular: false },
  { id: 'year', name: '年卡', description: '12个月VIP会员（推荐）', months: 12, price: 98, original_price: '144.00', is_popular: true },
];

export default function VipPage() {
  const { currentFamily } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vipInfo, setVipInfo] = useState<VIPInfo | null>(null);
  const [payments, setPayments] = useState<PaymentInfo[]>([]);
  const [benefits, setBenefits] = useState<BenefitItem[]>(defaultBenefits);
  const [packages, setPackages] = useState<PackageItem[]>(defaultPackages);
  const [showPayModal, setShowPayModal] = useState(false);

  // 加载数据
  useEffect(() => {
    if (!currentFamily?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const [vipRes, paymentsRes, rightsRes] = await Promise.all([
          vipApi.getVipInfo(currentFamily.id),
          paymentApi.getPaymentHistory(currentFamily.id),
          vipApi.getVipRights()
        ]);

        if (vipRes.code === 0 && vipRes.data) {
          setVipInfo(vipRes.data);
        }
        if (paymentsRes.code === 0 && paymentsRes.data) {
          setPayments(paymentsRes.data);
        }
        if (rightsRes.code === 0 && rightsRes.data) {
          const data = rightsRes.data as VipRightsData;
          if (data.free && data.free.length > 0) setBenefits(data.free);
          if (data.packages && data.packages.length > 0) setPackages(data.packages);
        }
      } catch (error) {
        console.error('Failed to load VIP data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentFamily?.id]);

  // 权益图标映射
  const benefitIcons: Record<string, { icon: string; color: string; bg: string }> = {
    '儿童数量': { icon: 'ri-user-smile-fill', color: 'text-blue-600', bg: 'bg-blue-50' },
    '奖惩规则': { icon: 'ri-list-check-3', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    '积分兑换': { icon: 'ri-gift-2-fill', color: 'text-amber-600', bg: 'bg-amber-50' },
    'AI对话': { icon: 'ri-robot-2-fill', color: 'text-violet-600', bg: 'bg-violet-50' },
    '广告': { icon: 'ri-advertisement-fill', color: 'text-teal-600', bg: 'bg-teal-50' },
  };

  // 获取年卡价格
  const yearPackage = packages.find(p => p.months === 12) || packages[0];
  const yearPrice = yearPackage ? Number(yearPackage.price) : 98;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-blue-500"></i>
            <p className="text-gray-400 mt-2">加载中...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isVip = vipInfo?.vip_status === 2 || vipInfo?.status === 1;
  const vipExpiry = vipInfo?.vip_expire_at ? new Date(vipInfo.vip_expire_at) : (vipInfo?.expire_at ? new Date(vipInfo.expire_at) : null);
  const daysRemaining = vipInfo?.days_remaining || (vipExpiry ? Math.ceil((vipExpiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 0);

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-2xl mx-auto">
        <div className="mb-5">
          <h1 className="text-xl font-black text-[#0d1f5c]">会员中心</h1>
          <p className="text-gray-400 text-sm mt-0.5">管理会员状态与权益</p>
        </div>

        {/* VIP Status Card */}
        {isVip ? (
          <div className="bg-gradient-to-br from-[#7c3d00] via-[#b45309] to-[#d97706] rounded-2xl p-6 mb-5 relative overflow-hidden">
            <div className="absolute -right-8 -top-8 w-44 h-44 bg-white/5 rounded-full"></div>
            <div className="absolute right-10 -bottom-10 w-32 h-32 bg-amber-300/10 rounded-full"></div>
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/20">
                    <i className="ri-vip-crown-fill text-amber-300 text-2xl"></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-white font-black text-xl">VIP 会员</h2>
                      <span className="bg-amber-300 text-amber-900 text-xs font-black px-2 py-0.5 rounded-full">已激活</span>
                    </div>
                    <p className="text-amber-200 text-sm">习惯星球高级会员</p>
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl px-3 py-1.5 text-center">
                  <p className="text-amber-200 text-xs mb-0.5">家庭账号</p>
                  <p className="text-white font-bold text-sm">{currentFamily?.name || '我的家庭'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/10 rounded-xl p-3">
                  <p className="text-amber-200 text-xs mb-0.5">剩余天数</p>
                  <p className="text-white font-bold text-sm">
                    {vipInfo?.days_remaining || daysRemaining}天
                  </p>
                </div>
                <div className="bg-white/10 rounded-xl p-3">
                  <p className="text-amber-200 text-xs mb-0.5">到期日期</p>
                  <p className="text-white font-bold text-sm">
                    {vipExpiry ? vipExpiry.toLocaleDateString() : '-'}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-white/20">
                <button
                  onClick={() => setShowPayModal(true)}
                  className="w-full bg-white text-amber-700 font-bold text-xs py-2.5 rounded-xl cursor-pointer hover:shadow-lg transition-all whitespace-nowrap"
                >
                  续费会员
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-200 rounded-2xl p-6 mb-5 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-200 mx-auto mb-3">
              <i className="ri-vip-crown-line text-gray-400 text-3xl"></i>
            </div>
            <h2 className="font-black text-[#0d1f5c] text-xl mb-1">普通用户</h2>
            <p className="text-gray-500 text-sm mb-4">升级VIP，解锁全部功能无限制使用</p>
            <button
              onClick={() => setShowPayModal(true)}
              className="bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold px-8 py-3 rounded-xl cursor-pointer hover:shadow-lg transition-all whitespace-nowrap"
            >
              立即开通 VIP · ¥{yearPrice}/年
            </button>
          </div>
        )}

        {/* Benefits Comparison */}
        <div className="bg-white rounded-2xl p-5 mb-4 border border-gray-50">
          <h3 className="font-bold text-[#0d1f5c] text-sm mb-4 flex items-center gap-2">
            <i className="ri-star-fill text-amber-400"></i>
            会员权益对比
          </h3>
          <div className="grid grid-cols-3 gap-0 mb-3 text-center">
            <div></div>
            <div className="py-2 bg-gray-50 rounded-tl-xl text-xs font-bold text-gray-500">免费版</div>
            <div className="py-2 bg-gradient-to-r from-amber-400 to-orange-500 rounded-tr-xl text-xs font-bold text-white flex items-center justify-center gap-1">
              <i className="ri-vip-crown-fill"></i>VIP
            </div>
          </div>
          {benefits.map((b, i) => {
            const iconConfig = benefitIcons[b.name] || { icon: 'ri-star-line', color: 'text-gray-600', bg: 'bg-gray-50' };
            return (
              <div key={i} className={`grid grid-cols-3 gap-0 text-center border-b border-gray-50 last:border-0`}>
                <div className="py-3 pr-2 flex items-center gap-2">
                  <div className={`w-6 h-6 flex items-center justify-center rounded-lg ${iconConfig.bg} flex-shrink-0`}>
                    <i className={`${iconConfig.icon} ${iconConfig.color} text-xs`}></i>
                  </div>
                  <span className="text-xs text-gray-600 text-left">{b.name}</span>
                </div>
                <div className={`py-3 bg-gray-50/50 flex items-center justify-center`}>
                  <span className="text-xs text-gray-400">{b.free_value}</span>
                </div>
                <div className={`py-3 bg-amber-50/50 flex items-center justify-center`}>
                  <span className="text-xs font-bold text-amber-700 flex items-center gap-0.5">
                    <i className="ri-check-fill text-emerald-500"></i>{b.vip_value}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Payment History */}
        <div className="bg-white rounded-2xl p-5 border border-gray-50">
          <h3 className="font-bold text-[#0d1f5c] text-sm mb-4 flex items-center gap-2">
            <i className="ri-bill-fill text-[#1a56db]"></i>
            支付记录
          </h3>
          {payments.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              <i className="ri-inbox-line text-3xl block mb-2"></i>
              暂无支付记录
            </div>
          ) : (
            <div className="space-y-2">
              {payments.map((record, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-50 flex-shrink-0">
                    <i className="ri-vip-crown-fill text-amber-500 text-base"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#0d1f5c] text-sm">{record.vip_months}个月VIP会员</div>
                    <div className="text-xs text-gray-400">{new Date(record.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-bold text-sm text-[#0d1f5c]">¥{record.amount}</div>
                    <div className="text-xs text-emerald-600">{record.status === 1 ? '已支付' : '待支付'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pay Modal */}
      {showPayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowPayModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[380px] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-5 text-center">
              <i className="ri-vip-crown-fill text-white text-4xl mb-2 block"></i>
              <h3 className="text-white font-black text-xl">开通 VIP 会员</h3>
              <p className="text-white/80 text-sm">解锁全部功能，畅享习惯星球</p>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-amber-50 rounded-xl p-4 text-center border border-amber-200">
                <p className="text-gray-500 text-xs mb-1">年费套餐</p>
                <div className="flex items-end justify-center gap-1">
                  <span className="text-gray-400 text-base">¥</span>
                  <span className="text-4xl font-black text-amber-600">98</span>
                  <span className="text-gray-400 text-base mb-1">/年</span>
                </div>
                <p className="text-xs text-amber-600 mt-1">折合每月约 ¥8.2 · 超划算！</p>
              </div>
              <div className="space-y-2">
                {vipBenefits.map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <i className="ri-check-fill text-emerald-500 flex-shrink-0"></i>
                    <span className="text-sm text-gray-600">{b.label}：<span className="font-bold text-[#0d1f5c]">{b.vip}</span></span>
                  </div>
                ))}
              </div>
              <button className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-white py-3.5 rounded-xl font-black text-base cursor-pointer hover:shadow-lg transition-all whitespace-nowrap">
                立即支付 ¥{yearPrice}
              </button>
              <button onClick={() => setShowPayModal(false)} className="w-full text-gray-400 text-sm cursor-pointer text-center">暂不升级</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
